import { Fragment, useCallback, type ComponentType } from "react";
import { Link } from "@remix-run/react";
import { useIntl } from "react-intl";

import EnergySlider from "~/components/EnergySlider";
import Search from "~/components/icons/Search";
import Location from "~/components/icons/Location";
import LanguageSwitcher from "~/components/shared/LanguageSwitcher";
import LoginStatus from "~/components/shared/LoginStatus";
import { useGlobal } from "~/context/global";
import { cn } from "~/utils/cn";
import { unreachableValue } from "~/utils/unreachable";
import { type MessageIds } from "~/lang";
import CineModeToggle from "~/components/shared/CineModeToggle";
import { type IconProps } from "~/components/icons";
import useLanguage from "~/hooks/useLanguage";

type BaseItemProps = {
	title: MessageIds;
	link: {
		de: string;
		en: string;
	};
	linkClassName?: string;
};

export type ItemLink = BaseItemProps & {
	type: "link";
	highlight?: boolean;
};

export type ItemIconLink = BaseItemProps & {
	type: "iconLink";
	icon: ComponentType<IconProps>;
};

export type Item =
	| { type: "group"; groupName: string; items: Array<Item> }
	| { type: "languageSwitcher" }
	| { type: "energySlider" }
	| { type: "loginStatus" }
	| { type: "cineModeToggle" }
	| { type: "toolsToggle" }
	| ItemLink
	| ItemIconLink;

export const SEARCH_ITEM: Item = {
	type: "iconLink",
	icon: Search,
	title: "toolbar.search",
	link: {
		de: "/suche/",
		en: "/en/search/",
	},
};

const ITEMS: Array<Item> = [
	{
		type: "group",
		groupName: "left",
		items: [
			{ type: "languageSwitcher" },
			{
				type: "link",
				highlight: true,
				title: "toolbar.application",
				link: {
					de: "/bewerben/",
					en: "/en/apply/",
				},
			},
			{
				type: "link",
				title: "toolbar.Lehrangebote",
				link: {
					de: "/lehrangebote/",
					en: "/en/courses/",
				},
			},
			{
				type: "link",
				title: "toolbar.Studienarbeiten",
				link: {
					de: "/projekte/",
					en: "/en/projects/",
				},
			},
		],
	},

	{
		type: "group",
		groupName: "center",
		items: [{ type: "energySlider" }],
	},

	{
		type: "group",
		groupName: "right",
		items: [
			{
				type: "link",
				title: "toolbar.media",
				link: {
					de: "/medien/",
					en: "/en/media/",
				},
			},
			{
				type: "link",
				title: "toolbar.press",
				link: {
					de: "/hochschule/presse/",
					en: "/en/university/press/",
				},
			},
			{ type: "toolsToggle" },

			{
				type: "group",
				groupName: "icons",
				items: [
					{
						type: "iconLink",
						linkClassName: "location-overlay-trigger",
						icon: Location,
						title: "toolbar.map",
						link: {
							de: "/hochschule/kontakt/anfahrtsbeschreibungen-lageplaene/",
							en: "/en/hochschule/kontakt/anfahrtsbeschreibungen-lageplaene/",
						},
					},
					SEARCH_ITEM,
					{ type: "loginStatus" },
					{ type: "cineModeToggle" },
				],
			},
		],
	},
];

export default function Toolbar() {
	const { isMobile } = useGlobal();

	return (
		<div
			id="toolbar"
			className={cn("module", { "!static !block": isMobile })}
		>
			<ul
				className={cn("inner-wrapper", {
					"!h-auto !flex-col !gap-[clamp(0rem,1.5vh,0.25rem)] !p-0":
						isMobile,
				})}
			>
				{ITEMS.map((item, itemIndex) => (
					<Fragment key={itemIndex}>
						{renderItem(item, isMobile)}
					</Fragment>
				))}
			</ul>
		</div>
	);
}

export function renderItem(item: Item, isMobile: boolean) {
	switch (item.type) {
		case "link":
			return <ItemLink {...item} />;

		case "group": {
			const items = item.items.map((child, childIndex) => (
				<Fragment key={childIndex}>
					{renderItem(child, isMobile)}
				</Fragment>
			));

			if (isMobile) {
				return items;
			} else {
				return (
					<li
						className={cn(
							"toolbar-item-group !grow",
							item.groupName,
						)}
					>
						<ul
							className={cn({
								"flex shrink-0 grow-0 flex-row flex-nowrap gap-[0.8rem]":
									!isMobile,
							})}
						>
							{items}
						</ul>
					</li>
				);
			}
		}

		case "energySlider":
			if (isMobile) {
				return null;
			} else {
				return (
					<li className="toolbar-item">
						<EnergySlider />
					</li>
				);
			}

		case "languageSwitcher":
			return (
				<li className="toolbar-item">
					<LanguageSwitcher />
				</li>
			);

		case "iconLink":
			return <ItemIconLink {...item} />;

		case "loginStatus":
			return (
				<li
					className={cn("toolbar-item", { "icon-button": !isMobile })}
				>
					<LoginStatus />
				</li>
			);

		case "cineModeToggle":
			if (isMobile) {
				return null;
			} else {
				return (
					<li
						className={cn("toolbar-item", {
							"icon-button": !isMobile,
						})}
					>
						<CineModeToggle />
					</li>
				);
			}

		case "toolsToggle":
			return <ToolsToggle />;

		default:
			unreachableValue(item);
	}
}

function ItemIconLink(item: ItemIconLink) {
	const { isMobile } = useGlobal();
	const intl = useIntl();
	const title = intl.formatMessage({ id: item.title });
	const lang = useLanguage();

	return (
		<li className={cn("toolbar-item", { "icon-button": !isMobile })}>
			<Link
				to={item.link[lang]}
				className={item.linkClassName}
				aria-label={title}
			>
				<item.icon label={title} />
				<span className="tooltip" aria-hidden="true">
					{title}
				</span>
			</Link>
		</li>
	);
}

function ItemLink(item: ItemLink) {
	const intl = useIntl();
	const title = intl.formatMessage({ id: item.title });
	const lang = useLanguage();

	return (
		<li
			className={cn("toolbar-item", item.linkClassName, {
				highlight: item.highlight,
			})}
		>
			<Link to={item.link[lang]} aria-label={title}>
				<span>{title}</span>
			</Link>
		</li>
	);
}

function ToolsToggle() {
	const intl = useIntl();
	const title = intl.formatMessage({ id: "toolbar.tools" });
	const { isMobile, openToolbarOverlay, setGlobal } = useGlobal();

	const handleClick = useCallback(() => {
		setGlobal((g) => {
			if (g.openToolbarOverlay === "tools") {
				g.openToolbarOverlay = null;
			} else {
				g.openToolbarOverlay = "tools";
			}
		});
	}, [setGlobal]);

	if (isMobile) {
		return null;
	}

	return (
		<li className="toolbar-item">
			<button
				onClick={handleClick}
				className={cn("toolbar-overlay-trigger", {
					active: openToolbarOverlay === "tools",
				})}
				aria-label={title}
			>
				<span>{title}</span>
			</button>
		</li>
	);
}
